const PATIENT_PRESENTATION_FEATURE_DEFAULTS = Object.freeze({
  videoAudioReview: false,
  spectrograms: false,
  rr: false,
  hr: false,
  hrv: false,
  fev1: false,
  fvc: false,
  pef: false,
  fev1Fvc: false,
  flowLoops: false,
  relativeChange: false,
  signalQuality: false,
  spirometryQuality: false,
  cardiacQuality: false,
  poincareChart: false,
  respiratorySignalChart: false,
  spirometrySegmentChart: false,
  averageFlowVolumeLoopChart: false,
  flowVolumeLoopsCharts: false,
  spirometryFlowLoop: false,
  recoveryDecay: false,
  breathingVariabilityPoincare: false,
  cumulativeBreathMagnitude: false,
  rollingRecoveryFlowVolumeLoops: false,
  environmentalContext: false,
  weatherDetails: false,
});

function createDashboardResultFeatureSet(overrides = {}) {
  return Object.freeze({
    ...PATIENT_PRESENTATION_FEATURE_DEFAULTS,
    ...overrides,
  });
}

const PRODUCT_PRESENTATION_PROFILE_RESULT_FALLBACKS = Object.freeze({
  "uk-ce-class-iia": createDashboardResultFeatureSet({
    videoAudioReview: true,
    spectrograms: true,
    rr: true,
    hr: true,
    fev1: true,
    fvc: true,
    pef: true,
    fev1Fvc: true,
    flowLoops: true,
    relativeChange: true,
    signalQuality: true,
    spirometryQuality: true,
    cardiacQuality: true,
    poincareChart: true,
    respiratorySignalChart: true,
    spirometrySegmentChart: true,
    averageFlowVolumeLoopChart: true,
    cumulativeBreathMagnitude: true,
    rollingRecoveryFlowVolumeLoops: true,
    environmentalContext: true,
    weatherDetails: true,
  }),
  "eu-ce-class-iia": createDashboardResultFeatureSet({
    videoAudioReview: true,
    spectrograms: true,
    rr: true,
    hr: true,
    fev1: true,
    fvc: true,
    pef: true,
    fev1Fvc: true,
    flowLoops: true,
    relativeChange: true,
    signalQuality: true,
    spirometryQuality: true,
    cardiacQuality: true,
    poincareChart: true,
    respiratorySignalChart: true,
    spirometrySegmentChart: true,
    averageFlowVolumeLoopChart: true,
    cumulativeBreathMagnitude: true,
    rollingRecoveryFlowVolumeLoops: true,
    environmentalContext: true,
    weatherDetails: true,
  }),
  "jp-research": createDashboardResultFeatureSet({
    videoAudioReview: true,
    signalQuality: true,
    spirometryQuality: true,
    cardiacQuality: true,
    environmentalContext: true,
    weatherDetails: true,
  }),
  "us-cds": createDashboardResultFeatureSet({
    videoAudioReview: true,
    environmentalContext: true,
    weatherDetails: false,
  }),
  "us-510k-denovo": createDashboardResultFeatureSet({
    videoAudioReview: true,
    spectrograms: true,
    rr: true,
    hr: true,
    fev1: true,
    fvc: true,
    pef: true,
    fev1Fvc: true,
    flowLoops: true,
    relativeChange: true,
    signalQuality: true,
    spirometryQuality: true,
    cardiacQuality: true,
    poincareChart: true,
    respiratorySignalChart: true,
    spirometrySegmentChart: true,
    averageFlowVolumeLoopChart: true,
    environmentalContext: true,
    weatherDetails: true,
  }),
  "au-research": createDashboardResultFeatureSet({
    videoAudioReview: true,
    spectrograms: true,
    rr: true,
    hr: true,
    signalQuality: true,
    spirometryQuality: true,
    cardiacQuality: true,
    environmentalContext: true,
    weatherDetails: true,
  }),
  "uk-demo": createDashboardResultFeatureSet({
    videoAudioReview: true,
    spectrograms: true,
    rr: true,
    hr: true,
    hrv: true,
    fev1: true,
    fvc: true,
    pef: true,
    fev1Fvc: true,
    flowLoops: true,
    relativeChange: true,
    signalQuality: true,
    spirometryQuality: true,
    cardiacQuality: true,
    poincareChart: true,
    respiratorySignalChart: true,
    spirometrySegmentChart: true,
    averageFlowVolumeLoopChart: true,
    flowVolumeLoopsCharts: true,
    spirometryFlowLoop: true,
    recoveryDecay: true,
    breathingVariabilityPoincare: true,
    cumulativeBreathMagnitude: true,
    rollingRecoveryFlowVolumeLoops: true,
    environmentalContext: true,
    weatherDetails: true,
  }),
});

const SPIROMETRY_OVERLAY_FEATURE_KEYS = Object.freeze([
  "fev1",
  "fvc",
  "fev1Fvc",
  "pef",
  "rr",
  "signalQuality",
  "spirometryQuality",
  "relativeChange",
  "respiratorySignalChart",
  "spirometrySegmentChart",
  "flowLoops",
  "averageFlowVolumeLoopChart",
  "flowVolumeLoopsCharts",
  "videoAudioReview",
]);

const CARDIOPULMONARY_OVERLAY_FEATURE_KEYS = Object.freeze([
  "hr",
  "hrv",
  "rr",
  "cardiacQuality",
  "signalQuality",
  "respiratorySignalChart",
  "poincareChart",
  "flowLoops",
  "averageFlowVolumeLoopChart",
  "flowVolumeLoopsCharts",
  "rollingRecoveryFlowVolumeLoops",
  "videoAudioReview",
]);

const POST_EXERTION_OVERLAY_FEATURE_KEYS = Object.freeze([
  "hr",
  "hrv",
  "rr",
  "cardiacQuality",
  "signalQuality",
  "respiratorySignalChart",
  "poincareChart",
  "flowLoops",
  "flowVolumeLoopsCharts",
  "rollingRecoveryFlowVolumeLoops",
  "videoAudioReview",
]);

const MEASUREMENT_TREND_FEATURE_KEYS = Object.freeze([
  "rr",
  "hr",
  "hrv",
  "fev1",
  "fvc",
  "pef",
  "fev1Fvc",
  "signalQuality",
  "spirometryQuality",
  "cardiacQuality",
  "relativeChange",
]);

const DASHBOARD_SOURCE_REVIEW_FEATURE_KEYS = new Set([
  "videoAudioReview",
  "spectrograms",
]);

const DASHBOARD_DERIVED_RESULT_FEATURE_KEYS = Object.freeze(
  Object.keys(PATIENT_PRESENTATION_FEATURE_DEFAULTS).filter(
    (key) => !DASHBOARD_SOURCE_REVIEW_FEATURE_KEYS.has(key),
  ),
);

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return "";
  }

  const normalizedValue = String(value).trim();
  return normalizedValue.length > 0 ? normalizedValue : "";
}

function normalizeProfileId(profileId) {
  return normalizeOptionalString(profileId).toLowerCase() || null;
}

export function resolveDashboardResultFeatures(productPresentationProfile) {
  if (!isPlainObject(productPresentationProfile)) {
    return null;
  }

  return Object.keys(PATIENT_PRESENTATION_FEATURE_DEFAULTS).reduce(
    (resolved, key) => {
      resolved[key] =
        productPresentationProfile?.dashboard?.results?.[key] === true;
      return resolved;
    },
    {},
  );
}

export function resolveFallbackDashboardResultFeatures(profileId) {
  const normalizedProfileId = normalizeProfileId(profileId);

  if (!normalizedProfileId) {
    return null;
  }

  const fallbackFeatures =
    PRODUCT_PRESENTATION_PROFILE_RESULT_FALLBACKS[normalizedProfileId];

  return fallbackFeatures ? { ...fallbackFeatures } : null;
}

function mergeDashboardResultFeatureAccess(
  dashboardResultFeatures,
  productPresentationFeatureAccess,
) {
  if (!isPlainObject(dashboardResultFeatures)) {
    return null;
  }

  const configuredFeatureAccess =
    productPresentationFeatureAccess?.dashboard?.results || null;

  if (!isPlainObject(configuredFeatureAccess)) {
    return { ...dashboardResultFeatures };
  }

  return Object.keys(PATIENT_PRESENTATION_FEATURE_DEFAULTS).reduce(
    (resolved, key) => {
      const configuredValue = configuredFeatureAccess[key];

      resolved[key] =
        dashboardResultFeatures[key] === true &&
        (typeof configuredValue === "boolean" ? configuredValue : true);
      return resolved;
    },
    {},
  );
}

export function resolveProductPresentationDashboardResultFeatures(
  productPresentationProfile,
  { fallbackProfileId = null, featureAccess = null } = {},
) {
  const normalizedFallbackProfileId = normalizeProfileId(fallbackProfileId);
  const normalizedLiveProfileId = normalizeProfileId(productPresentationProfile?.id);
  const shouldPreferFallbackProfile =
    normalizedFallbackProfileId &&
    normalizedLiveProfileId !== normalizedFallbackProfileId;

  const liveDashboardResultFeatures = shouldPreferFallbackProfile
    ? null
    : resolveDashboardResultFeatures(productPresentationProfile);
  const fallbackDashboardResultFeatures =
    resolveFallbackDashboardResultFeatures(fallbackProfileId);
  const baseDashboardResultFeatures =
    liveDashboardResultFeatures || fallbackDashboardResultFeatures;

  return mergeDashboardResultFeatureAccess(
    baseDashboardResultFeatures,
    featureAccess,
  );
}

export function isDashboardResultFeatureVisible(dashboardResultFeatures, key) {
  if (!key) {
    return true;
  }

  if (!isPlainObject(dashboardResultFeatures)) {
    return false;
  }

  return dashboardResultFeatures[key] === true;
}

function hasEnabledFeature(
  dashboardResultFeatures,
  keys = Object.keys(PATIENT_PRESENTATION_FEATURE_DEFAULTS),
) {
  if (!isPlainObject(dashboardResultFeatures)) {
    return false;
  }

  return keys.some((key) => dashboardResultFeatures[key] === true);
}

export function isDashboardReviewOnlyResultFeatures(dashboardResultFeatures) {
  if (!isPlainObject(dashboardResultFeatures)) {
    return false;
  }

  return (
    dashboardResultFeatures.videoAudioReview === true &&
    !DASHBOARD_DERIVED_RESULT_FEATURE_KEYS.some(
      (key) => dashboardResultFeatures[key] === true,
    )
  );
}

export function hasVisibleMeasurementTrendFeatures(dashboardResultFeatures) {
  return hasEnabledFeature(dashboardResultFeatures, MEASUREMENT_TREND_FEATURE_KEYS);
}

export function classifyPatientRecordingEntry(entry) {
  const variant = normalizeOptionalString(entry?.variant || entry?.collectionName);

  if (!variant) {
    return "unknown";
  }

  if (variant === "RecordingSpirometry") {
    return "spirometry";
  }

  if (variant === "RecordingPostExertion") {
    return "post-exertion";
  }

  if (variant === "RecordingManualAudio") {
    return "manual-audio";
  }

  if (variant === "RecordingPulseScan") {
    return "pulse";
  }

  return "cardiopulmonary";
}

export function canOpenPatientRecordingEntry(entry, dashboardResultFeatures) {
  const kind = classifyPatientRecordingEntry(entry);

  if (kind === "spirometry") {
    return hasEnabledFeature(
      dashboardResultFeatures,
      SPIROMETRY_OVERLAY_FEATURE_KEYS,
    );
  }

  if (kind === "manual-audio") {
    return hasEnabledFeature(dashboardResultFeatures, ["videoAudioReview"]);
  }

  if (kind === "post-exertion") {
    return hasEnabledFeature(
      dashboardResultFeatures,
      POST_EXERTION_OVERLAY_FEATURE_KEYS,
    );
  }

  return hasEnabledFeature(
    dashboardResultFeatures,
    CARDIOPULMONARY_OVERLAY_FEATURE_KEYS,
  );
}

function featureSpecMatches(dashboardResultFeatures, featureSpec) {
  if (!featureSpec) {
    return true;
  }

  if (Array.isArray(featureSpec)) {
    return featureSpec.some((key) =>
      isDashboardResultFeatureVisible(dashboardResultFeatures, key),
    );
  }

  return isDashboardResultFeatureVisible(dashboardResultFeatures, featureSpec);
}

function filterPresentationItems(items, dashboardResultFeatures) {
  return (Array.isArray(items) ? items : []).filter((item) =>
    featureSpecMatches(dashboardResultFeatures, item?.featureKey),
  );
}

export function filterPatientRecordingPresentation(
  presentation,
  dashboardResultFeatures,
) {
  if (!presentation) {
    return null;
  }

  if (!isPlainObject(dashboardResultFeatures)) {
    return {
      ...presentation,
      metrics: [],
      statuses: [],
      trendMetrics: [],
      charts: [],
    };
  }

  return {
    ...presentation,
    metrics: filterPresentationItems(presentation.metrics, dashboardResultFeatures),
    statuses: filterPresentationItems(
      presentation.statuses,
      dashboardResultFeatures,
    ),
    trendMetrics: filterPresentationItems(
      presentation.trendMetrics,
      dashboardResultFeatures,
    ),
    charts: filterPresentationItems(presentation.charts, dashboardResultFeatures),
  };
}
